import { getAEMPageUrl } from '@aem/utils';
import { Directive, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ContentPageId } from '@shared/models';
import { AEMService } from '@shared/services/aem/aem.service';
import { environment } from '@src/environments';
import * as fromRootStore from '@store/index';
import { catchError, map, Observable, tap } from 'rxjs';
import { SubSink } from 'subsink';

import { LoggerService } from '../../modules/logger/logger';

interface AEMConfig<AEMResponse, PageContent> {
  aemService: AEMService;
  toPageContent: (ac: AEMResponse, store?: Store<fromRootStore.State>) => PageContent;
}

/**
 * Leverage base page to consolidate common functionality on content and reduce code duplication.
 */
@Directive()
export class PageComponent<AEMResponse = unknown, PageContent = unknown>
  implements OnInit, OnDestroy
{
  content$: Observable<any>;

  protected subs = new SubSink();
  remoteContent$?: Observable<PageContent>;
  remoteContentUrl?: string;

  constructor(
    protected pageName: ContentPageId,
    protected globalStore: Store<fromRootStore.State>,
    protected aemConfig?: AEMConfig<AEMResponse, PageContent>,

    protected loggerService?: LoggerService,
  ) {
    this.content$ = this.globalStore.pipe(select('content', 'entity', this.pageName as any));
    this.remoteContentUrl = getAEMPageUrl(pageName, environment.name);
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  ngOnInit() {
    this.loadAem();
  }

  nextPage() {
    this.globalStore.dispatch(fromRootStore.Next());
  }

  previousPage() {
    this.globalStore.dispatch(fromRootStore.Back());
  }

  /**
   * Given that a page is migrated to use remote content (AEM), this method will:
   * 1. Fetch remote content from AEM
   * 2. If the fetch fails it will retrieve fallback content (json file from assets/)
   * 3. Maps the AEM response to the page content format
   */
  loadAem() {
    /* istanbul ignore next */
    if (this.aemConfig && this.remoteContentUrl) {
      const aemConfig = this.aemConfig;
      this.remoteContent$ = aemConfig.aemService
        .getContent<AEMResponse>(this.remoteContentUrl)
        .pipe(
          catchError((error) => {
            this.globalStore.dispatch(
              fromRootStore.LogError({
                payload: {
                  eventId: 'aem-akamai-failure',
                  pageName: this.pageName,
                  error,
                },
              }),
            );
            return aemConfig.aemService.getFallbackContent<AEMResponse>(this.pageName);
          }),
          map((aemContent$) => aemConfig.toPageContent(aemContent$, this.globalStore)),
          tap(() => {
            this.globalStore.dispatch(
              fromRootStore.LogInfo({
                payload: {
                  eventId: 'aem-content-success',
                  pageName: this.pageName,
                },
              }),
            );
          }),
        );
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
